<template>
  <div class="assayMix" ref="scrollable">
    <h1 class="title_text padding page-title">
      Смешивание проб
    </h1>

    <div class="headerBlock-metal padding">
      <SelectMetal v-model="selectedMetal" />
    </div>

    <div class="grid padding">
      <!-- left top -->
      <div class="grid-item">
        <div class="grid-item-header">
          <div class="grid-item-title">Проба основного металла</div>
          <div class="grid-item-switch">
            Своя
            <CastomSwitcher
              id="assaymix-own-base"
              v-model="isOwnBaseAssay"
            />
          </div>
        </div>

        <div class="grid-item-body">
          <CastomInput
            v-if="isOwnBaseAssay"
            :defaultValue="0"
            style="width: 120px;"
            v-model="inputs.baseAssay"
            :required="isRequired"
            ref="resetBaseAssay"
          />
          <CastomSelect
            v-else
            :placeholder="'Проба'"
            :options="assayOptions"
            style="width: 160px;"
            v-model="inputs.baseAssay"
            :required="isRequired"
          />
        </div>
      </div>

      <!-- right top -->
      <div class="grid-item grid-item--mass">
        <div class="grid-item-header">
          <div class="grid-item-title">Вес основного металла, г.</div>
        </div>
        <div class="grid-item-body">
          <CastomInput
            fontSize="16px"
            v-model="inputs.baseMass"
            ref="resetBaseMass"
            :required="isRequired"
          />
        </div>
      </div>

      <!-- left bottom -->
      <div class="grid-item">
        <div class="grid-item-header">
          <div class="grid-item-title">Проба лигатуры</div>
          <div class="grid-item-switch">
            Своя
            <CastomSwitcher
              id="assaymix-own-ligature"
              v-model="isOwnLigatureAssay"
            />
          </div>
        </div>
        <div class="grid-item-body">
          <CastomInput
            v-if="isOwnLigatureAssay"
            :defaultValue="0"
            style="width: 120px;"
            v-model="inputs.ligatureAssay"
            :required="isRequired"
            ref="resetLigatureAssay"
          />
          <CastomSelect
            v-else
            :placeholder="'Проба'"
            :options="ligatureAssayOptions"
            style="width: 160px;"
            v-model="inputs.ligatureAssay"
            :required="isRequired"
          />
        </div>
      </div>

      <!-- right bottom -->
      <div class="grid-item">
        <div class="grid-item-header">
          <div class="grid-item-title">Желаемая проба</div>
          <div class="grid-item-switch">
            Своя
            <CastomSwitcher
              id="assaymix-own-desired"
              v-model="isOwnDesiredAssay"
            />
          </div>
        </div>
        <div class="grid-item-body">
          <CastomInput
            v-if="isOwnDesiredAssay"
            :defaultValue="0"
            style="width: 120px;"
            v-model="inputs.desiredAssay"
            :required="isRequired"
            ref="resetDesiredAssay"
          />
          <CastomSelect
            v-else
            :placeholder="'Проба'"
            :options="assayOptions"
            style="width: 160px;"
            v-model="inputs.desiredAssay"
            :required="isRequired"
          />
        </div>
      </div>
    </div>

    <button
      @click="calculateClick"
      class="button"
    >
      Рассчитать
    </button>

    <div class="errorBox" v-if="errorText">
      {{ errorText }}
    </div>

    <div class="footer flexColumn" v-if="ligatureMass || newAlloyMass">
      <div class="footer-container flexRow" v-if="ligatureMass">
        <div class="footer-text fs14">
          Масса лигатуры
        </div>
        <div class="footer-number fs14">
          {{ ligatureMass }}
        </div>
        <div class="footer-text fs14">
          г
        </div>
      </div>
      <div class="footer-container flexRow" v-if="ligatureCost">
        <div class="footer-text fs14">
          Стоимость лигатуры
        </div>
        <div class="footer-number fs14">
          {{ formatPrice(ligatureCost) }}
        </div>
        <div class="footer-text fs14">
          ₽
        </div>
      </div>
      <div class="footer-container flexRow" v-if="newAlloyMass">
        <div class="footer-text fs14">
          Новая масса сплава
        </div>
        <div class="footer-number fs14">
          {{ newAlloyMass }}
        </div>
        <div class="footer-text fs14">
          г
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue"
import SelectMetal from "@/components/selects/SelectMetal"
import CastomSelect from "@/components/selects/CastomSelect"
import CastomInput from "@/components/inputs/CastomInput"
import CastomSwitcher from "@/components/switchers/CastomSwitcher"
import metalDensity from "@/jsonData/metalDensity.json"
import { useMetalsStore } from "@/stores/metalsPrice/metalsStore"
import { formatPrice } from "@/helpers/helpers"
import { trackCalculation } from '@/helpers/calculationAnalytics'

const selectedMetal = ref('silver')
const isRequired = ref(false)
const metalsStore = useMetalsStore()
const errorText = ref('')

const isOwnBaseAssay = ref(false)
const isOwnLigatureAssay = ref(false)
const isOwnDesiredAssay = ref(false)

const resetBaseMass = ref(null)
const resetBaseAssay = ref(null)
const resetLigatureAssay = ref(null)
const resetDesiredAssay = ref(null)

const scrollable = ref(null)

const inputs = reactive({
  baseAssay: 0, // number | {name,value}
  ligatureAssay: 0, // number | {name,value}
  desiredAssay: 0, // number | {name,value}
  baseMass: 0, // г
})

const ligatureMass = ref(0)
const newAlloyMass = ref(0)

if (window.gtag) {
  window.gtag('event', 'assay-mix_calculator', {})
}

const assayOptions = computed(() => {
  const list = selectedMetal.value === 'silver' ? metalDensity.silver : metalDensity.gold
  return list.map(o => ({ name: o.name, value: Number(o.value) }))
})

const ligatureAssayOptions = computed(() => {
  const base = assayOptions.value
  const extras = [
    { name: '999 (чистый металл)', value: 999 },
  ]
  const map = new Map()
  ;[...extras, ...base].forEach(o => {
    if (!map.has(o.value)) map.set(o.value, o)
  })
  return Array.from(map.values())
})

const baseAssayValue = computed(() => {
  if (isOwnBaseAssay.value) return Number(inputs.baseAssay) || 0
  return inputs.baseAssay?.value ? Number(inputs.baseAssay.value) : 0
})
const ligatureAssayValue = computed(() => {
  if (isOwnLigatureAssay.value) return Number(inputs.ligatureAssay) || 0
  return inputs.ligatureAssay?.value ? Number(inputs.ligatureAssay.value) : 0
})
const desiredAssayValue = computed(() => {
  if (isOwnDesiredAssay.value) return Number(inputs.desiredAssay) || 0
  return inputs.desiredAssay?.value ? Number(inputs.desiredAssay.value) : 0
})
const baseMassValue = computed(() => Number(inputs.baseMass) || 0)

const metalRate = computed(() => {
  if (!metalsStore?.data) return 0
  return selectedMetal.value === 'gold'
    ? Number(metalsStore.data.gold || 0)
    : Number(metalsStore.data.silver || 0)
})

const ligatureCost = computed(() => {
  const w = Number(ligatureMass.value) || 0
  const rate = metalRate.value || 0
  const assay = Number(ligatureAssayValue.value) || 0
  if (!w || !rate || !assay) return null
  const cost = w * rate * (assay / 1000)
  return Number.isFinite(cost) ? cost.toFixed(2) : null
})

const isValid = computed(() => {
  return baseMassValue.value > 0 &&
    baseAssayValue.value >= 0 &&
    ligatureAssayValue.value >= 0 &&
    desiredAssayValue.value >= 0 &&
    baseAssayValue.value <= 999 &&
    ligatureAssayValue.value <= 999 &&
    desiredAssayValue.value <= 999
})

const scrollToBottom = () => {
  if (scrollable.value) {
    scrollable.value.scrollTo({
      top: scrollable.value.scrollHeight,
      behavior: 'smooth',
    })
  }
}

const resetResults = () => {
  ligatureMass.value = 0
  newAlloyMass.value = 0
  if (!inputs.baseMass && resetBaseMass.value?.resetValue) resetBaseMass.value.resetValue()
}

const calculateClick = () => {
  setTimeout(() => {
    scrollToBottom()
  }, 500)

  isRequired.value = true
  errorText.value = ''
  if (!isValid.value) {
    resetResults()
    return
  }

  const p0 = baseAssayValue.value
  const pL = ligatureAssayValue.value
  const pD = desiredAssayValue.value
  const m0 = baseMassValue.value

  const denom = (pL - pD)
  if (!denom) {
    resetResults()
    return
  }

  const x = m0 * (pD - p0) / denom
  if (!Number.isFinite(x) || x < 0) {
    resetResults()
    errorText.value = 'Не удалось выполнить расчет. Пожалуйста, перепроверьте вводимые данные.'
    return
  }

  ligatureMass.value = parseFloat(x.toFixed(2))
  newAlloyMass.value = parseFloat((m0 + x).toFixed(2))
  trackCalculation('assay_mix')
}

watch(selectedMetal, () => {
  inputs.baseAssay = 0
  inputs.ligatureAssay = 0
  inputs.desiredAssay = 0
  errorText.value = ''
  resetResults()
})

watch([isOwnBaseAssay, isOwnLigatureAssay, isOwnDesiredAssay], () => {
  errorText.value = ''
  resetResults()
})
</script>

<style lang="scss" scoped>
.assayMix {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  align-items: center;
  text-align: center;
  height: calc(100vh - 140px);
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 10px;
}

.headerBlock {
  &-metal {
    width: 100%;
  }
}

.grid {
  margin-top: 25px;
  padding-right: 20px;
  padding-left: 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.grid-item {
  width: 80%;
  aspect-ratio: 1 / 1;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.65);
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 2px 2px rgba(0, 0, 0, 0.15);
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  color: white;
  gap: 10px;

  &-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  &-title {
    font-size: 13px;
    line-height: 1.2;
  }

  &-subtitle {
    font-size: 12px;
    opacity: 0.85;
  }

  &-switch {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 12px;
    opacity: 0.95;
  }

  &-body {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex: 1;
    margin-top: auto;
    padding-bottom: 6px;
  }
}

.grid-item--mass .grid-item-body {
  padding-bottom: 6px;
}

.footer {
  margin-top: 20px;
  margin-bottom: 20px;
  border-radius: 25px;
  border: 2px solid #fff;
  height: auto;
  padding: 10px;
  width: 90%;
  box-shadow: 0 2px 2px 2px rgba(0, 0, 0, 0.25);
  background: linear-gradient(180deg, rgba(181, 162, 189, 0.33) 0%, rgba(26, 21, 28, 0.33) 100%);
  gap: 7px;

  &-container {
    color: white;
    justify-content: flex-start
  }
  &-text {
    font-size: 18px;
    font-weight: bold;
    margin-left: 10px;
  }
  &-number {
    font-size: 18px;
    font-weight: bold;
    margin-left: auto;
  }
}

.fs14 {
  font-size: 14px;
}

.padding {
  border: 2px solid white;
  border-left: none;
  border-right: none;
  border-bottom: none;
  padding: 10px 0;
}

.flexColumn {
  display: flex;
  flex-direction: column;
}

.flexRow {
  display: flex;
  flex-direction: row;
}

.title_text {
  color: white;
  width: 100%;
}

.button {
  background: rgba(0, 0, 0, 0.22);
  color: rgba(255, 255, 255, 0.98);
  width: 100%;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 14px 20px;
  margin-top: 20px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.08s ease, filter 0.18s ease;
  user-select: none;
}

.button:active {
  transform: translateY(1px) scale(0.99);
  filter: brightness(0.96);
}

.button:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.95);
  outline-offset: 2px;
}

.errorBox {
  width: 90%;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 2px solid rgba(255, 80, 80, 0.9);
  background: rgba(0, 0, 0, 0.18);
  color: rgba(255, 255, 255, 0.98);
  font-size: 13px;
  line-height: 1.25;
}

@media (max-width: 420px) {
  .grid {
    gap: 10px;
    padding-right: 10px;
    padding-left: 10px;
  }
  .grid-item {
    padding: 10px;
  }
  .grid-item-title {
    font-size: 12px;
  }
}
</style>

